// frontend/src/views/MaintenanceView.jsx

import React, { useState, useEffect } from 'react';
import { 
    ClipboardCheck, Wrench, Thermometer, CircleCheck, Upload, FileText, CircleAlert, User, TriangleAlert, ArrowLeft 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function MaintenanceView({ qrKey }) {
    const { user } = useAuth();
    const [printerData, setPrinterData] = useState(null);
    const [lastRecord, setLastRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        printer_id: null, new_print_hours: '', performed_by: '', notes: '', pm_type: 'Routine',
        enclosure_cleaned: false, extruder_cleaned: false, fans_checked: false, carbon_rods_cleaned: false,
        lead_screws_greased: false, verification_complete: false, quality_check_pass: false, is_repair: false, repair_notes: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);

    const getStatus = (lastDateStr, lastHours, currentHours, dayLimit, hourLimit) => {
        if (!lastDateStr) return { color: '#ef4444', text: 'Never Done' };
        const today = new Date();
        const diffTime = Math.abs(today - new Date(lastDateStr));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (hourLimit > 0) {
            const hoursSince = (currentHours || 0) - (lastHours || 0);
            if (hoursSince >= hourLimit) return { color: '#ef4444', text: `Due (+${hoursSince.toFixed(0)}h)` };
        }
        if (diffDays >= dayLimit) return { color: '#ef4444', text: `Due (${diffDays}d ago)` };
        if (diffDays >= dayLimit - 2) return { color: '#f59e0b', text: `Due Soon` };
        return { color: '#10b981', text: 'OK' };
    };

    useEffect(() => {
        const fetchPrinterData = async () => {
            setLoading(true);
            try {
                const response = await fetch(`/api/printers/scan/${qrKey}`);
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Printer not found.');
                
                let pData = data.printer || data;
                let lRecord = data.last_record || { hours: data.last_recorded_hours, performed_by: data.last_performed_by, date: data.maintenance_date };
                const safeId = pData.id || pData.printer_id;

                setPrinterData(pData);
                setLastRecord(lRecord);
                setFormData(prev => ({ ...prev, printer_id: safeId, new_print_hours: pData.current_print_hours ? pData.current_print_hours.toString() : '', notes: data.last_notes || '', performed_by: user?.name || '' }));
            } catch (err) { console.error(err); setError(err.message); } 
            finally { setLoading(false); }
        };
        if (qrKey) fetchPrinterData();
    }, [qrKey, user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleFileChange = (e) => { if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        let detectedPmType = 'Biweekly';
        if (formData.is_repair) detectedPmType = 'Repair';
        else if (formData.lead_screws_greased) detectedPmType = 'Quarterly';
        else if (formData.carbon_rods_cleaned) detectedPmType = 'Monthly';

        const submitPayload = new FormData();
        Object.keys(formData).forEach(key => { if (key === 'pm_type') submitPayload.append(key, detectedPmType); else submitPayload.append(key, formData[key]); });
        const prevHours = lastRecord ? lastRecord.hours : (printerData ? printerData.current_print_hours : 0);
        submitPayload.append('previous_print_hours', prevHours);
        if (selectedFile) submitPayload.append('maintenance_photo', selectedFile);

        try {
            const response = await fetch('/api/printers/maintenance', { method: 'POST', body: submitPayload });
            if (!response.ok) throw new Error('Failed to submit record.');
            setFormSubmitted(true);
        } catch (err) { setError(err.message); } 
        finally { setLoading(false); }
    };

    const MaintenanceItem = ({ label, frequency, name, tierStatus }) => {
        const safeStatus = tierStatus || { color: '#ccc', text: 'Loading...' };
        return (
            <label style={{ display:'flex', alignItems:'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input type="checkbox" name={name} checked={formData[name]} onChange={handleChange} style={{ width: '1.2rem', height: '1.2rem', accentColor: '#0d9488' }} /> 
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{label}</span>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{frequency}</span>
                    </div>
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: safeStatus.color, whiteSpace: 'nowrap' }}>{safeStatus.text}</span>
            </label>
        );
    };

    if (loading) return <div className="container text-center" style={{paddingTop:'50px'}}><p>Loading Printer Data...</p></div>;
    if (error) return <div className="container"><div className="card text-red"><CircleAlert /> <strong>Error: {error}</strong></div></div>;
    if (formSubmitted) return <div className="container"><div className="card text-center"><CircleCheck size={48} color="var(--success)" /><h2 className="text-green">Log Saved!</h2><p>Record saved successfully.</p><button onClick={() => window.location.href = '/'} className="secondary">Return to Fleet</button></div></div>;

    const currentHours = parseFloat(formData.new_print_hours) || printerData?.current_print_hours || 0;
    const statEnclosure = getStatus(printerData?.last_enclosure_date, 0, currentHours, 14, 0);
    const statExtruder = getStatus(printerData?.last_extruder_date, 0, currentHours, 14, 0);
    const statFans = getStatus(printerData?.last_fans_date, 0, currentHours, 14, 0);
    const statRods = getStatus(printerData?.last_carbon_rods_date, printerData?.last_carbon_rods_hours, currentHours, 30, 200);
    const statScrews = getStatus(printerData?.last_lead_screws_date, printerData?.last_lead_screws_hours, currentHours, 90, 800);

    return (
        <div className="container">
            {/* ?? ADDED: Back Button at Top */}
            <button className="secondary" onClick={() => window.location.href='/'} style={{marginBottom:'15px', display:'flex', alignItems:'center', gap:'5px', width:'auto', padding:'6px 12px'}}>
                <ArrowLeft size={18}/> Back to Fleet
            </button>

            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid var(--border)', paddingBottom: '1rem', marginBottom: '1rem' }}>
                    <Wrench size={24} color="var(--primary)" />
                    <div>
                        <h2 style={{ margin: 0 }}>{printerData?.name}</h2>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>{printerData?.model} | S/N: {printerData?.serial_number}</p>
                    </div>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-section" style={{ backgroundColor: '#f8fafc' }}>
                        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}><Thermometer size={18}/> Meter Reading</h3>
                        <label>Current Hours</label>
                        <input type="number" name="new_print_hours" step="0.1" value={formData.new_print_hours} onChange={handleChange} required />
                        <label>Performed By</label>
                        <input type="text" value={formData.performed_by} readOnly style={{ backgroundColor: '#e2e8f0', color: '#475569' }} />
                    </div>
                    <div className="form-section">
                        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'0' }}><ClipboardCheck size={18}/> Tasks</h3>
                        <p style={{ fontSize:'0.8rem', color:'#64748b', marginBottom:'15px' }}>Check items as you complete them.</p>
                        <MaintenanceItem label="Clean Enclosure" frequency="14 Days" name="enclosure_cleaned" tierStatus={statEnclosure} />
                        <MaintenanceItem label="Clean Extruder" frequency="14 Days" name="extruder_cleaned" tierStatus={statExtruder} />
                        <MaintenanceItem label="Check Fans" frequency="14 Days" name="fans_checked" tierStatus={statFans} />
                        <MaintenanceItem label="Clean Carbon Rods" frequency="30 Days / 200h" name="carbon_rods_cleaned" tierStatus={statRods} />
                        <MaintenanceItem label="Grease Lead Screws" frequency="90 Days / 800h" name="lead_screws_greased" tierStatus={statScrews} />
                    </div>
                    <div className="form-section" style={{ border:'1px solid #f59e0b', backgroundColor:'#fffbeb' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'8px', color:'#b45309', fontWeight:'bold', cursor:'pointer' }}>
                            <TriangleAlert size={18}/> 
                            <input type="checkbox" name="is_repair" checked={formData.is_repair} onChange={handleChange} style={{width:'1.2rem', height:'1.2rem', accentColor:'#d97706'}} />
                            Unscheduled Repair?
                        </label>
                        {formData.is_repair && <textarea name="repair_notes" placeholder="Describe repair details..." value={formData.repair_notes} onChange={handleChange} rows="3" style={{ marginTop:'10px', borderColor:'#fcd34d' }} />}
                    </div>
                    <div className="form-section">
                        <h3 style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}><CircleCheck size={18}/> Verification</h3>
                        <label style={{marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px'}}><input type="checkbox" name="quality_check_pass" checked={formData.quality_check_pass} onChange={handleChange} style={{width:'1.2rem', height:'1.2rem'}} /> Run Quality Calibration?</label>
                        <label style={{marginBottom:'15px', display:'flex', alignItems:'center', gap:'8px'}}><input type="checkbox" name="verification_complete" checked={formData.verification_complete} onChange={handleChange} required style={{width:'1.2rem', height:'1.2rem'}} /> <strong>I verify this work is complete.</strong></label>
                        <label style={{fontWeight:'bold', display:'flex', alignItems:'center', gap:'5px'}}><Upload size={16}/> Photo Evidence</label>
                        <input type="file" accept="image/*" onChange={handleFileChange} style={{marginTop:'5px'}} />
                    </div>
                    <div className="form-section">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={16}/> Notes</label>
                        <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} placeholder="General notes..." />
                    </div>
                    <button type="submit">Submit Maintenance Log</button>
                </form>
            </div>
        </div>
    );
}

export default MaintenanceView;